import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Building2, Users, TrendingUp, Badge, Star, ShieldCheck, Megaphone, Globe2 } from "lucide-react";
import countries from "../utils/countries";
import React, { useState } from "react";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {SEO} from "@/components/SEO.tsx";

interface Social {
  instagram?: string
  facebook?: string;
  twitter?: string;
  linkedin?: string;
  tiktok?: string;
  reddit?: string;
  youtube?: string;
  discord?: string;
}

interface BusinessFormValues {
  brandName: string;
  tradeName: string;
  nationality: string;
  website: string;
  logo: string;
  socialMedia: Social;
  email: string;
  password: string;
}

const Business = () => {
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const businessFormSchema = z.object({
    brandName: z.string().min(2, "Brand name must be at least 2 characters long"),
    tradeName: z.string().min(2, "Trade name must be at least 2 characters long"),
    nationality: z.string().min(2, "Nationality is required"),
    website: z.string().url("Invalid website URL"),
    logo: z.string().url("Invalid logo URL"),
    socialMedia: z
        .object({
          facebook: z.string().url("Invalid Facebook URL").optional(),
          twitter: z.string().url("Invalid Twitter URL").optional(),
          instagram: z.string().url("Invalid Instagram URL").optional(),
          linkedin: z.string().url("Invalid LinkedIn URL").optional(),
        })
        .optional(), // Tüm socialMedia alanını opsiyonel yaptık
    email: z.string().email("Invalid email address"),
    password: z.string()
        .min(8, "Password must be at least 8 characters long")
        .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
        .regex(/[0-9]/, "Password must contain at least one number")
  });
  type FormData = z.infer<typeof businessFormSchema>;

  const form = useForm<FormData>({
    defaultValues: {
      brandName: "",
      tradeName: "",
      nationality: "",
      website: "",
      logo: "",
      socialMedia: {},
      email: "",
      password: "",
    },
    resolver: zodResolver(businessFormSchema)
  });

  const onSubmit = async (data: BusinessFormValues) => {
    // console.log(data)
    try {
      const url = `${import.meta.env.VITE_API_URL}/partners`;
      const headers = {
        'Content-Type': 'application/json',
      };
      const body = JSON.stringify(data);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData.success) {
        // console.log('İşlem başarılı:', responseData.message)
      } else {
        // console.error('İşlem başarısız:', responseData.message);
      }
    } catch (error) {
      // console.error('Hata:', error);
    }
  };

  const handleAgreementClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = '/terms?tab=partner';
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
          title="Business"
          description="Partner with us and connect your brand with an engaged poll audience"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Side - Registration Form */}
          <div>
            <Card className="p-6">
              <h1 className="text-3xl font-bold mb-8">Join as Business Partner</h1>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold">Company Information</h2>
                      <p className="text-secondary-light mt-1 mb-4">Tell us about your company and establish your business presence.</p>
                    </div>
                    <FormField
                      control={form.control}
                      name="brandName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Brand Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your brand name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="tradeName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Trade Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Legal trade name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="nationality"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nationality</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-white dark:bg-black">
                                <SelectValue placeholder="Select your country" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {countries.map((country) => (
                                <SelectItem key={country.code} value={country.code}>
                                  {country.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Website</FormLabel>
                          <FormControl>
                            <Input placeholder="https://your-website.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="logo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Logo URL</FormLabel>
                          <FormControl>
                            <Input placeholder="URL to your company logo" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="socialMedia.linkedin"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>LinkedIn (Optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="LinkedIn profile URL" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="socialMedia.twitter"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Twitter (Optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Twitter profile URL" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold">Account Information</h2>
                      <p className="text-secondary-light mt-1 mb-4">Create your business account credentials to access the platform.</p>
                    </div>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="business@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="Create a secure password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="agreement"
                        checked={agreementAccepted}
                        onCheckedChange={(checked) => setAgreementAccepted(checked as boolean)}
                      />
                      <label
                        htmlFor="agreement"
                        className="text-sm text-muted-foreground"
                      >
                        I have read and agree to the{" "}
                        <a
                          href="/terms?tab=partner"
                          onClick={handleAgreementClick}
                          className="text-primary hover:underline"
                        >
                          Partner Agreement
                        </a>
                      </label>
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={!agreementAccepted}
                    >
                      Register as Business
                    </Button>
                  </div>
                </form>
              </Form>
            </Card>
          </div>

          {/* Right Side - Benefits & Testimonials */}
          <div className="space-y-12">
            <div className="space-y-8">
              <h2 className="text-2xl font-bold">Why Choose blankpoll for Business?</h2>

              <div className="grid gap-6">
                <div className="flex gap-4 items-center">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Badge className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Verified Users</h3>
                    <p className="text-secondary-light">Access to a network of verified users ensuring quality engagement with your polls.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Brand Awareness & Insights</h3>
                    <p className="text-secondary-light">Gain valuable insights and increase brand visibility through engaging polls.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Multi-Channel Reach</h3>
                    <p className="text-secondary-light">Connect with millions of users across various channels and demographics.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <ShieldCheck className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Secure & Reliable Data</h3>
                    <p className="text-secondary-light">Get accurate and reliable market insights with our secure polling system.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Megaphone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Enhanced Marketing Impact</h3>
                    <p className="text-secondary-light">Leverage poll results for targeted marketing campaigns and better ROI.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Globe2 className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Global Market Analysis</h3>
                    <p className="text-secondary-light">Access insights, promotional opportunities, and product analysis across 190 countries worldwide.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold">What Our Partners Say</h2>
              <div className="grid gap-6">
                <Card className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">Tech Solutions Inc</h4>
                        <div className="flex text-yellow-400">
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                        </div>
                      </div>
                      <p className="text-secondary-light">"blankpoll has significantly improved our market research process. The insights we've gained are invaluable."</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">Global Brands Co</h4>
                        <div className="flex text-yellow-400">
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                        </div>
                      </div>
                      <p className="text-secondary-light">"The quality of user engagement and the reach we've achieved through blankpoll has exceeded our expectations."</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">Innovation Labs Ltd</h4>
                        <div className="flex text-yellow-400">
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                          <Star className="w-4 h-4 fill-current" />
                        </div>
                      </div>
                      <p className="text-secondary-light">"Using blankpoll has transformed how we collect customer feedback. The platform's reach and user engagement are outstanding."</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Business;
